import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import DeleteIcon from "@mui/icons-material/Delete";
import {
	Box,
	Button,
	Divider,
	Grid2,
	IconButton,
	TextField,
	Typography,
} from "@mui/material";
import { useEffect, useMemo } from "react";
import {
	Control,
	Controller,
	FieldArrayWithId,
	UseFormSetValue,
	useFieldArray,
	useWatch,
} from "react-hook-form";
import { Product } from "@/shared/models/Product";
import { PunchBeamPlate } from "@/shared/models/PunchBeamPosition";
import { parseNumberInput } from "@/shared/utils/parseNumberInput";

type Props = {
	control: Control<Product>;
	setValue: UseFormSetValue<Product>;
	disabled: boolean;
};

const SectionHeader = ({ title }: { title: string }) => (
	<Grid2 size={{ xs: 12 }}>
		<Typography
			variant="subtitle2"
			fontWeight={600}
			color="primary"
			sx={{ mb: 0.5, mt: 1 }}
		>
			{title}
		</Typography>
		<Divider />
	</Grid2>
);

type BeamField = FieldArrayWithId<Product, "BeamPositions", "id">;
type PerfField = FieldArrayWithId<Product, "Perforations", "id">;

type PlateSectionProps = {
	control: Control<Product>;
	disabled: boolean;
	plate: PunchBeamPlate;
	title: string;
	fields: BeamField[];
	slotIndices: number[];
	onClearSlot: (index: number) => void;
};

const PlateSection = ({
	control,
	disabled,
	title,
	fields,
	slotIndices,
	onClearSlot,
}: PlateSectionProps) => {
	return (
		<Grid2 size={{ xs: 12 }}>
			<Typography
				variant="subtitle2"
				fontWeight={600}
				color="primary"
				sx={{ mb: 0.5, mt: 1 }}
			>
				{title}
			</Typography>
			<Divider sx={{ mb: 1 }} />
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					mb: 0.5,
				}}
			>
				<Typography variant="caption" color="text.secondary">
					(Avenue [South] Side)
				</Typography>
				<Typography variant="caption" color="text.secondary">
					(River [North] Side)
				</Typography>
			</Box>
			<Box sx={{ display: "flex", gap: 1 }}>
				{slotIndices.map((idx) => (
					<Controller
						key={fields[idx].id}
						name={`BeamPositions.${idx}.PositionValue`}
						control={control}
						render={({ field }) => (
							<TextField
								type="number"
								value={field.value || ""}
								onChange={(e) => {
									const parsed = parseNumberInput(e.target.value, { min: 1 });
									field.onChange(parsed == null ? "" : String(parsed));
								}}
								onBlur={() => {
									field.onBlur();
									if (field.value) {
										const n = parseFloat(field.value);
										if (Number.isFinite(n)) {
											const s = String(Math.round(n * 1000) / 1000);
											if (s !== field.value) field.onChange(s);
										}
									}
								}}
								size="small"
								disabled={disabled}
								sx={{ flex: 1 }}
								inputProps={{ min: 1, step: 0.001 }}
								InputProps={{
									endAdornment: field.value ? (
										<IconButton
											size="small"
											onClick={() => onClearSlot(idx)}
											disabled={disabled}
											tabIndex={-1}
											sx={{ p: 0.25 }}
										>
											<ClearIcon fontSize="inherit" />
										</IconButton>
									) : null,
								}}
							/>
						)}
					/>
				))}
			</Box>
		</Grid2>
	);
};

type HoleRowsProps = {
	control: Control<Product>;
	disabled: boolean;
	fields: PerfField[];
	slotIndices: number[];
	onAdd: () => void;
	onRemove: (index: number) => void;
};

const HoleRows = ({
	control,
	disabled,
	fields,
	slotIndices,
	onAdd,
	onRemove,
}: HoleRowsProps) => {
	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
			{slotIndices.map((i) => (
				<Box
					key={fields[i].id}
					sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
				>
					<Controller
						name={`Perforations.${i}.HoleSize`}
						control={control}
						render={({ field }) => (
							<TextField
								label="Hole size/type"
								type="number"
								value={field.value ?? ""}
								onChange={(e) =>
									field.onChange(parseNumberInput(e.target.value, { min: 0 }))
								}
								size="small"
								disabled={disabled}
								inputProps={{ min: 0, step: "0.0001" }}
								sx={{ flex: 1 }}
							/>
						)}
					/>
					<Controller
						name={`Perforations.${i}.DetailNumber`}
						control={control}
						render={({ field }) => (
							<TextField
								label="Detail #"
								value={field.value || ""}
								onChange={(e) => field.onChange(e.target.value || null)}
								size="small"
								disabled={disabled}
								sx={{ flex: 1 }}
							/>
						)}
					/>
					<Controller
						name={`Perforations.${i}.PunchPartNumber`}
						control={control}
						render={({ field }) => (
							<TextField
								label="Punch Part #"
								value={field.value || ""}
								onChange={(e) => field.onChange(e.target.value || null)}
								size="small"
								disabled={disabled}
								sx={{ flex: 1.5 }}
							/>
						)}
					/>
					<IconButton
						size="small"
						color="secondary"
						onClick={() => onRemove(i)}
						disabled={disabled}
						sx={{ flexShrink: 0 }}
					>
						<DeleteIcon fontSize="small" />
					</IconButton>
				</Box>
			))}
			<Box>
				<Button
					size="small"
					variant="outlined"
					startIcon={<AddIcon />}
					onClick={onAdd}
					disabled={disabled}
				>
					Add Hole
				</Button>
			</Box>
		</Box>
	);
};

type ButtonRowsProps = {
	control: Control<Product>;
	disabled: boolean;
	fields: PerfField[];
	slotIndices: number[];
	onAdd: () => void;
	onRemove: (index: number) => void;
};

const ButtonRows = ({
	control,
	disabled,
	fields,
	slotIndices,
	onAdd,
	onRemove,
}: ButtonRowsProps) => {
	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
			{slotIndices.map((i) => (
				<Box
					key={fields[i].id}
					sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
				>
					<Controller
						name={`Perforations.${i}.DetailNumber`}
						control={control}
						render={({ field }) => (
							<TextField
								label="Detail #"
								value={field.value || ""}
								onChange={(e) => field.onChange(e.target.value || null)}
								size="small"
								disabled={disabled}
								sx={{ flex: 1 }}
							/>
						)}
					/>
					<Controller
						name={`Perforations.${i}.ButtonPartNumber`}
						control={control}
						render={({ field }) => (
							<TextField
								label="Button Part #"
								value={field.value || ""}
								onChange={(e) => field.onChange(e.target.value || null)}
								size="small"
								disabled={disabled}
								sx={{ flex: 2 }}
							/>
						)}
					/>
					<IconButton
						size="small"
						color="secondary"
						onClick={() => onRemove(i)}
						disabled={disabled}
						sx={{ flexShrink: 0 }}
					>
						<DeleteIcon fontSize="small" />
					</IconButton>
				</Box>
			))}
			<Box>
				<Button
					size="small"
					variant="outlined"
					startIcon={<AddIcon />}
					onClick={onAdd}
					disabled={disabled}
				>
					Add Button Hole
				</Button>
			</Box>
		</Box>
	);
};

export const PunchSpecsFields = ({ control, setValue, disabled }: Props) => {
	const beamArray = useFieldArray({ control, name: "BeamPositions" });
	const perfArray = useFieldArray({ control, name: "Perforations" });

	const bundleCol = useWatch({ control, name: "BundleCol" });
	const bundleRow = useWatch({ control, name: "BundleRow" });

	useEffect(() => {
		const col = bundleCol ?? 0;
		const row = bundleRow ?? 0;
		const count = col && row ? col * row : null;
		setValue("BundleCount", count, { shouldDirty: true });
	}, [bundleCol, bundleRow, setValue]);

	const upperPlateIndices = useMemo(() => {
		const out: number[] = [];
		for (let order = 1; order <= 7; order++) {
			const idx = beamArray.fields.findIndex(
				(f) => f.Plate === "U" && f.PositionOrder === order,
			);
			if (idx !== -1) out.push(idx);
		}
		return out;
	}, [beamArray.fields]);

	const lowerPlateIndices = useMemo(() => {
		const out: number[] = [];
		for (let order = 1; order <= 7; order++) {
			const idx = beamArray.fields.findIndex(
				(f) => f.Plate === "L" && f.PositionOrder === order,
			);
			if (idx !== -1) out.push(idx);
		}
		return out;
	}, [beamArray.fields]);

	const holeIndices = useMemo(
		() =>
			perfArray.fields
				.map((f, i) => ({ f, i }))
				.filter(({ f }) => f.PerforationType === "HOLE")
				.map(({ i }) => i),
		[perfArray.fields],
	);

	const buttonIndices = useMemo(
		() =>
			perfArray.fields
				.map((f, i) => ({ f, i }))
				.filter(({ f }) => f.PerforationType === "BUTTON")
				.map(({ i }) => i),
		[perfArray.fields],
	);

	const handleClearBeamSlot = (idx: number) => {
		const { id: _id, ...current } = beamArray.fields[idx];
		beamArray.update(idx, { ...current, PositionValue: "" });
	};

	// Each punched hole needs its button die, so adding a hole adds both rows.
	const handleAddHole = () =>
		perfArray.append([
			{
				PunchPerforationId: null,
				ProductId: 0,
				PerforationType: "HOLE",
				SequenceOrder: 0,
				HoleSize: null,
				DetailNumber: null,
				PunchPartNumber: null,
				ButtonPartNumber: null,
			},
			{
				PunchPerforationId: null,
				ProductId: 0,
				PerforationType: "BUTTON",
				SequenceOrder: 0,
				HoleSize: null,
				DetailNumber: null,
				PunchPartNumber: null,
				ButtonPartNumber: null,
			},
		]);

	const handleAddButton = () =>
		perfArray.append({
			PunchPerforationId: null,
			ProductId: 0,
			PerforationType: "BUTTON",
			SequenceOrder: 0,
			HoleSize: null,
			DetailNumber: null,
			PunchPartNumber: null,
			ButtonPartNumber: null,
		});

	return (
		<>
			{/* Beam section */}
			<SectionHeader title="Beam" />
			<Grid2 size={{ xs: 6, md: 3 }}>
				<Controller
					name="UpperRiserDrawingNum"
					control={control}
					render={({ field }) => (
						<TextField
							label="Upper Riser Drwg #"
							value={field.value || ""}
							onChange={(e) => field.onChange(e.target.value || null)}
							fullWidth
							size="small"
							disabled={disabled}
						/>
					)}
				/>
			</Grid2>
			<Grid2 size={{ xs: 6, md: 3 }}>
				<Controller
					name="LowerRiserDrawingNum"
					control={control}
					render={({ field }) => (
						<TextField
							label="Lower Riser Drwg. #"
							value={field.value || ""}
							onChange={(e) => field.onChange(e.target.value || null)}
							fullWidth
							size="small"
							disabled={disabled}
						/>
					)}
				/>
			</Grid2>
			<Grid2 size={{ xs: 6, md: 3 }}>
				<Controller
					name="StopBlockNumber"
					control={control}
					render={({ field }) => (
						<TextField
							label="Stop Block #"
							value={field.value || ""}
							onChange={(e) => field.onChange(e.target.value || null)}
							fullWidth
							size="small"
							disabled={disabled}
						/>
					)}
				/>
			</Grid2>

			{/* Upper | Lower Die Plates side-by-side */}
			<PlateSection
				control={control}
				disabled={disabled}
				plate="U"
				title="Upper Die Plates"
				fields={beamArray.fields}
				slotIndices={upperPlateIndices}
				onClearSlot={handleClearBeamSlot}
			/>
			<PlateSection
				control={control}
				disabled={disabled}
				plate="L"
				title="Lower Die Plates"
				fields={beamArray.fields}
				slotIndices={lowerPlateIndices}
				onClearSlot={handleClearBeamSlot}
			/>

			{/* Perforations: Hole | Button side-by-side */}
			<SectionHeader title="Perforations" />
			<Grid2 size={{ xs: 12, md: 6 }}>
				<Typography
					variant="caption"
					color="text.secondary"
					sx={{ display: "block", mb: 0.5 }}
				>
					Hole
				</Typography>
				<HoleRows
					control={control}
					disabled={disabled}
					fields={perfArray.fields}
					slotIndices={holeIndices}
					onAdd={handleAddHole}
					onRemove={perfArray.remove}
				/>
			</Grid2>
			<Grid2 size={{ xs: 12, md: 6 }}>
				<Typography
					variant="caption"
					color="text.secondary"
					sx={{ display: "block", mb: 0.5 }}
				>
					Button
				</Typography>
				<ButtonRows
					control={control}
					disabled={disabled}
					fields={perfArray.fields}
					slotIndices={buttonIndices}
					onAdd={handleAddButton}
					onRemove={perfArray.remove}
				/>
			</Grid2>

			{/* Bundle Configuration */}
			<SectionHeader title="Bundle Configuration" />
			<Grid2 size={{ xs: 4, md: 1.7 }}>
				<Controller
					name="BundleCol"
					control={control}
					render={({ field }) => (
						<TextField
							label="Columns"
							type="number"
							value={field.value ?? ""}
							onChange={(e) =>
								field.onChange(
									parseNumberInput(e.target.value, { integer: true, min: 1 }),
								)
							}
							fullWidth
							size="small"
							disabled={disabled}
							inputProps={{ min: 1, step: 1 }}
						/>
					)}
				/>
			</Grid2>
			<Grid2 size={{ xs: 4, md: 1.7 }}>
				<Controller
					name="BundleRow"
					control={control}
					render={({ field }) => (
						<TextField
							label="Rows"
							type="number"
							value={field.value ?? ""}
							onChange={(e) =>
								field.onChange(
									parseNumberInput(e.target.value, { integer: true, min: 1 }),
								)
							}
							fullWidth
							size="small"
							disabled={disabled}
							inputProps={{ min: 1, step: 1 }}
						/>
					)}
				/>
			</Grid2>
			<Grid2 size={{ xs: 4, md: 2 }}>
				<Controller
					name="BundleCount"
					control={control}
					render={({ field }) => (
						<TextField
							label="Count"
							type="number"
							value={field.value ?? ""}
							fullWidth
							size="small"
							disabled
						/>
					)}
				/>
			</Grid2>
			<Grid2 size={{ xs: 4, md: 2.5 }}>
				<Controller
					name="StickSize"
					control={control}
					render={({ field }) => (
						<TextField
							label="Stick Size"
							value={field.value || ""}
							onChange={(e) => field.onChange(e.target.value || null)}
							fullWidth
							size="small"
							disabled={disabled}
						/>
					)}
				/>
			</Grid2>
		</>
	);
};
